import { useMemo } from "react";
import { ArrowUpRight, Trophy, Sparkles, Clock } from "lucide-react";

// Mock "Transfer Window" — recently converted / won deals. Drives both the
// celebratory banner and the sports-analytics sidebar on the Leader Dashboard.
const RECENT_TRANSFERS = [
  {
    id: "xfer-01",
    name: "River Bend Campground",
    sector: "Campground · Family Rec",
    rep: "Alpha · Direct",
    value: "$19.95/mo · Pilot",
    signedAt: "2h ago",
    tier: "Marquee",
  },
  {
    id: "xfer-02",
    name: "Delaware HVAC Co.",
    sector: "Trades · HVAC",
    rep: "Field North",
    value: "$19.95/mo",
    signedAt: "Yesterday",
    tier: "Standard",
  },
  {
    id: "xfer-03",
    name: "Marion Mobile Mechanics",
    sector: "Auto · Mobile",
    rep: "Field North",
    value: "$19.95/mo",
    signedAt: "2d ago",
    tier: "Standard",
  },
  {
    id: "xfer-04",
    name: "Bucyrus Legal Advisory",
    sector: "Professional Services",
    rep: "Alpha · Direct",
    value: "$19.95/mo",
    signedAt: "3d ago",
    tier: "Standard",
  },
];

const TIER_ACCENT = {
  Marquee: "#10B981",
  Standard: "#60A5FA",
};

export function GridTransferBanner() {
  const latest = RECENT_TRANSFERS[0];
  return (
    <div
      className="relative overflow-hidden rounded-sm border mb-6"
      style={{
        borderColor: "rgba(16,185,129,0.55)",
        background:
          "linear-gradient(90deg, rgba(16,185,129,0.22) 0%, rgba(16,185,129,0.08) 55%, rgba(6,11,20,0.6) 100%)",
        boxShadow: "0 0 44px -18px rgba(16,185,129,0.7)",
      }}
      data-testid="grid-transfer-banner"
    >
      {/* Top accent stripe */}
      <div
        className="absolute inset-x-0 top-0 h-px"
        style={{
          background:
            "linear-gradient(90deg, transparent, rgba(16,185,129,0.9), transparent)",
        }}
      />
      {/* Subtle grain */}
      <div
        className="absolute inset-0 opacity-[0.08] pointer-events-none"
        style={{
          background:
            "radial-gradient(circle at 15% 20%, rgba(255,255,255,0.6) 0px, transparent 1px), radial-gradient(circle at 65% 70%, rgba(255,255,255,0.4) 0px, transparent 1px)",
          backgroundSize: "18px 18px, 26px 26px",
        }}
      />

      <div className="relative px-6 py-5 flex flex-col md:flex-row md:items-center gap-4">
        <div
          className="w-10 h-10 rounded-sm border flex items-center justify-center shrink-0"
          style={{
            borderColor: "rgba(16,185,129,0.6)",
            background: "rgba(16,185,129,0.14)",
          }}
        >
          <Trophy size={18} style={{ color: "#10B981" }} />
        </div>

        <div className="flex-1 min-w-0">
          <div
            className="text-[10px] uppercase tracking-[0.42em] mb-1 flex items-center gap-2"
            style={{ color: "#10B981" }}
          >
            <Sparkles size={10} /> Grid Transfer · Just In
          </div>
          <div className="font-display text-2xl sm:text-3xl text-white leading-tight">
            HERE WE GO!{" "}
            <span style={{ color: "#10B981" }}>{latest.name}</span> just crossed
            the line.
          </div>
          <div className="text-slate-300 text-xs mt-1 font-mono-aviation">
            {latest.sector} · Signed by {latest.rep} · {latest.signedAt}
          </div>
        </div>

        <div className="flex items-center gap-3 shrink-0">
          <div
            className="px-3 py-2 rounded-sm text-[10px] uppercase tracking-[0.28em]"
            style={{
              background: "rgba(16,185,129,0.14)",
              border: "1px solid rgba(16,185,129,0.55)",
              color: "#10B981",
            }}
          >
            {latest.value}
          </div>
        </div>
      </div>
    </div>
  );
}

export function TransferWindow() {
  const marquee = useMemo(
    () => RECENT_TRANSFERS.filter((t) => t.tier === "Marquee").length,
    []
  );
  const total = RECENT_TRANSFERS.length;

  return (
    <aside
      className="rounded-sm border p-5"
      style={{
        borderColor: "rgba(16,185,129,0.28)",
        background:
          "linear-gradient(180deg, rgba(16,185,129,0.06) 0%, rgba(6,11,20,0.7) 100%)",
      }}
      data-testid="transfer-window"
    >
      <div className="flex items-center justify-between mb-4">
        <div>
          <div
            className="text-[10px] uppercase tracking-[0.34em] mb-1"
            style={{ color: "#10B981" }}
          >
            Transfer Window
          </div>
          <div className="font-display text-lg text-white leading-tight">
            Recent Conversions
          </div>
        </div>
        <div className="text-right">
          <div className="font-display text-2xl" style={{ color: "#10B981" }}>
            {total}
          </div>
          <div className="text-[9px] uppercase tracking-[0.28em] text-slate-500">
            {marquee} marquee
          </div>
        </div>
      </div>

      <ul className="space-y-3" data-testid="transfer-window-list">
        {RECENT_TRANSFERS.map((t) => {
          const accent = TIER_ACCENT[t.tier] || "#60A5FA";
          return (
            <li
              key={t.id}
              className="rounded-sm border p-3 relative"
              style={{
                borderColor: `${accent}33`,
                background: "rgba(6,11,20,0.6)",
              }}
              data-testid={`transfer-item-${t.id}`}
            >
              <div className="flex items-start justify-between gap-2 mb-1.5">
                <div className="min-w-0">
                  <div className="font-display text-sm text-white truncate">
                    {t.name}
                  </div>
                  <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 mt-0.5">
                    {t.sector}
                  </div>
                </div>
                <ArrowUpRight
                  size={14}
                  style={{ color: accent }}
                  className="shrink-0 mt-0.5"
                />
              </div>
              <div className="flex items-center justify-between text-[10px]">
                <span
                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm uppercase tracking-[0.22em]"
                  style={{
                    background: `${accent}18`,
                    color: accent,
                    border: `1px solid ${accent}44`,
                  }}
                >
                  {t.tier}
                </span>
                <span className="text-slate-500 font-mono-aviation flex items-center gap-1">
                  <Clock size={9} /> {t.signedAt}
                </span>
              </div>
              <div className="mt-1.5 text-[10px] text-slate-400 font-mono-aviation">
                {t.rep} · {t.value}
              </div>
            </li>
          );
        })}
      </ul>
    </aside>
  );
}
